import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import DataTable from '../../components/Common/DataTable';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { 
  PlusIcon, PencilIcon, TrashIcon, EyeIcon, ArrowDownTrayIcon,
  FunnelIcon, XMarkIcon, CalendarIcon
} from '@heroicons/react/24/outline';

const Files = () => {
  const [files, setFiles] = useState([]);
  const [pageInfo, setPageInfo] = useState({
    totalElements: 0, totalPages: 0, currentPage: 0, size: 10
  });
  
  const [filters, setFilters] = useState({
    fromDtTm: '',
    toDtTm: ''
  });
  
  const [showFilters, setShowFilters] = useState(false);
  
  const { loading, get, del } = useApi();
  const navigate = useNavigate();

  useEffect(() => { fetchFiles(); }, [filters, pageInfo.currentPage]);

  const fetchFiles = async () => {
    const params = new URLSearchParams();
    params.append('page', pageInfo.currentPage);
    params.append('size', pageInfo.size);
    
    if (filters.fromDtTm) params.append('fromDtTm', filters.fromDtTm);
    if (filters.toDtTm) params.append('toDtTm', filters.toDtTm);
    
    const endpoint = `/api/files/filter?${params.toString()}`;
    const { data } = await get(endpoint);
    if (data) {
      setFiles(data.content || []);
      setPageInfo({
        totalElements: data.totalElements, totalPages: data.totalPages,
        currentPage: data.number || 0, size: data.size || 10
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      const { error } = await del(`/api/files/${id}`);
      if (!error) fetchFiles();
    }
  };

  const handleDownload = (file) => {
    if (file.path) window.open(file.path, '_blank');
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPageInfo(prev => ({ ...prev, currentPage: 0 }));
  };

  const clearFilters = () => {
    setFilters({ fromDtTm: '', toDtTm: '' });
    setPageInfo(prev => ({ ...prev, currentPage: 0 }));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  const columns = [
    { header: 'ID', accessor: 'id' },
    { header: 'File Name', accessor: (row) => row.path?.split('/').pop() || '-' },
    { header: 'Type', accessor: (row) => row.type?.fileName || '-' },
    { header: 'Size', accessor: (row) => formatFileSize(row.size) },
    { header: 'Encrypted', accessor: (row) => (
      <span className={`px-2 py-1 rounded-full text-xs ${row.isEncrypted ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
        {row.isEncrypted ? 'Yes' : 'No'}
      </span>
    )},
    { header: 'Timestamp', accessor: (row) => row.timeStamp ? new Date(row.timeStamp).toLocaleString() : '-' },
    { header: 'Actions', accessor: (row) => (
      <div className="flex space-x-2">
        <button onClick={() => navigate(`/files/${row.id}`)} className="text-blue-600 hover:text-blue-900"><EyeIcon className="h-5 w-5" /></button>
        <button onClick={() => handleDownload(row)} className="text-green-600 hover:text-green-900"><ArrowDownTrayIcon className="h-5 w-5" /></button>
        <button onClick={() => navigate(`/files/edit/${row.id}`)} className="text-indigo-600 hover:text-indigo-900"><PencilIcon className="h-5 w-5" /></button>
        <button onClick={() => handleDelete(row.id)} className="text-red-600 hover:text-red-900"><TrashIcon className="h-5 w-5" /></button>
      </div>
    )}
  ];

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Files</h1>
        <button onClick={() => navigate('/files/add')} className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2 w-full sm:w-auto justify-center">
          <PlusIcon className="h-5 w-5" /><span>Upload File</span>
        </button>
      </div>

      <div className="mb-4">
        <div className="flex gap-2">
          <button type="button" onClick={() => setShowFilters(!showFilters)} className={`px-4 py-2 border rounded-lg flex items-center space-x-2 ${showFilters ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
            <FunnelIcon className="h-5 w-5" /><span className="hidden sm:inline">Filters</span>
          </button>
          {(filters.fromDtTm || filters.toDtTm) && (
            <button type="button" onClick={clearFilters} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center space-x-2">
              <XMarkIcon className="h-5 w-5" /><span className="hidden sm:inline">Clear</span>
            </button>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center"><FunnelIcon className="h-4 w-4 mr-1" />Date Range Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-xs font-medium text-gray-600 mb-1 flex items-center"><CalendarIcon className="h-3 w-3 mr-1" />From Date</label>
              <input type="datetime-local" name="fromDtTm" value={filters.fromDtTm || ''} onChange={handleFilterChange} className="input-field text-sm" />
            </div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1 flex items-center"><CalendarIcon className="h-3 w-3 mr-1" />To Date</label>
              <input type="datetime-local" name="toDtTm" value={filters.toDtTm || ''} onChange={handleFilterChange} className="input-field text-sm" />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-xs text-gray-500 mr-2 self-center">Quick dates:</span>
            <button onClick={() => {
              const today = new Date(); const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
              setFilters(prev => ({ ...prev, fromDtTm: yesterday.toISOString().slice(0, 16), toDtTm: today.toISOString().slice(0, 16) }));
            }} className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200">Last 24 Hours</button>
            <button onClick={() => {
              const today = new Date(); const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
              setFilters(prev => ({ ...prev, fromDtTm: weekAgo.toISOString().slice(0, 16), toDtTm: today.toISOString().slice(0, 16) }));
            }} className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200">Last 7 Days</button>
            <button onClick={() => {
              const today = new Date(); const monthAgo = new Date(); monthAgo.setMonth(monthAgo.getMonth() - 1);
              setFilters(prev => ({ ...prev, fromDtTm: monthAgo.toISOString().slice(0, 16), toDtTm: today.toISOString().slice(0, 16) }));
            }} className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200">Last 30 Days</button>
          </div>
        </div>
      )}

      <div className="mb-4 text-sm text-gray-600">
        Showing {files.length} of {pageInfo.totalElements} files
      </div>

      <DataTable columns={columns} data={files} pageInfo={pageInfo} onPageChange={(page) => setPageInfo({...pageInfo, currentPage: page})} />
    </div>
  );
};

export default Files;